"use client";

import { Check, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { useRailScrollRestore } from "@/hooks/use-rail-scroll-restore";
import { cn } from "@/lib/utils";

type FilterRailProps = {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  orientation?: "horizontal" | "vertical";
  /** Remembers how far the chips were scrolled across a back navigation. */
  restoreKey?: string;
};

type FilterRailItemTheme = "brand" | "light";
type FilterRailItemSize = "default" | "compact" | "chip";

type FilterRailItemProps = {
  active: boolean;
  badge?: ReactNode;
  className?: string;
  description?: string;
  icon?: ReactNode;
  label: string;
  onClick: () => void;
  size?: FilterRailItemSize;
  theme?: FilterRailItemTheme;
};

export function FilterRail({
  children,
  className,
  contentClassName,
  orientation = "horizontal",
  restoreKey,
}: FilterRailProps) {
  const railRef = useRailScrollRestore<HTMLDivElement>(
    orientation === "horizontal" ? restoreKey : undefined,
  );

  return (
    <div
      ref={railRef}
      className={cn(
        orientation === "horizontal"
          ? "scrollbar-hide overflow-x-auto"
          : "overflow-visible",
        className,
      )}
    >
      <div
        className={cn(
          orientation === "horizontal"
            ? "flex min-w-max gap-2 pb-1"
            : "grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2",
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
}

export function FilterRailItem({
  active,
  badge,
  className,
  description,
  icon,
  label,
  onClick,
  size = "default",
  theme = "brand",
}: FilterRailItemProps) {
  const isLight = theme === "light";

  if (size === "chip") {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={cn(
          "group shrink-0 rounded-full border text-left transition-all duration-200",
          "px-3 py-1.5",
          active
            ? isLight
              ? "border-border bg-surface text-text-primary shadow-sm dark:border-white/24 dark:bg-white/14 dark:text-white"
              : "border-black/10 bg-white/82 text-text-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.85),0_7px_20px_rgba(15,23,42,0.08)] backdrop-blur-xl dark:border-white/16 dark:bg-white/[0.12] dark:text-white"
            : isLight
              ? "border-border bg-surface/80 text-text-secondary hover:bg-surface dark:border-glass-border-10 dark:bg-glass-bg-05 dark:text-white/72 dark:hover:bg-glass-bg-08"
              : "border-black/8 bg-white/58 text-text-secondary backdrop-blur-xl hover:bg-white/76 dark:border-white/10 dark:bg-white/[0.055] dark:text-white/72 dark:hover:bg-white/[0.09]",
          className,
        )}
      >
        <div className="flex items-center gap-1.5">
          {icon ? (
            <span className="flex h-4 w-4 shrink-0 items-center justify-center">
              {icon}
            </span>
          ) : null}
          <span className="text-[13px] font-medium whitespace-nowrap">
            {label}
          </span>
          {badge ? (
            <span
              className={cn(
                "inline-flex shrink-0 items-center rounded-full px-1.5 text-[10px] font-medium",
                active
                  ? isLight
                    ? "bg-surface-alt text-text-primary dark:bg-glass-bg-12 dark:text-white"
                    : "bg-black/6 text-text-primary dark:bg-white/10 dark:text-white"
                  : isLight
                    ? "bg-surface-alt text-text-secondary dark:bg-glass-bg-08 dark:text-white/60"
                    : "bg-surface-alt text-text-secondary",
              )}
            >
              {badge}
            </span>
          ) : null}
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "group shrink-0 rounded-2xl border text-left transition-all duration-200",
        size === "default"
          ? "min-w-[13rem] px-3.5 py-3"
          : "min-w-[11rem] px-3 py-2.5",
        active
          ? isLight
            ? "border-border bg-surface text-text-primary shadow-sm dark:border-white/24 dark:bg-white/14 dark:text-white dark:shadow-[0_14px_36px_rgba(0,0,0,0.22)]"
            : "border-black/10 bg-white/82 text-text-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.85),0_12px_32px_rgba(15,23,42,0.1)] backdrop-blur-xl dark:border-white/16 dark:bg-white/[0.12] dark:text-white"
          : isLight
            ? "border-border bg-surface/80 text-text-secondary hover:bg-surface dark:border-glass-border-10 dark:bg-glass-bg-05 dark:text-white/72 dark:hover:bg-glass-bg-08"
            : "border-black/8 bg-white/58 text-text-secondary backdrop-blur-xl hover:bg-white/76 dark:border-white/10 dark:bg-white/[0.055] dark:text-white/72 dark:hover:bg-white/[0.09]",
        className,
      )}
    >
      <div className="flex items-center gap-2.5">
        <div
          className={cn(
            "flex shrink-0 items-center justify-center rounded-xl border transition-colors",
            size === "default" ? "h-10 w-10" : "h-9 w-9",
            active
              ? isLight
                ? "border-border bg-surface-alt text-text-primary dark:border-white/16 dark:bg-glass-bg-12 dark:text-white"
                : "border-black/8 bg-white/72 text-text-primary shadow-sm dark:border-white/12 dark:bg-white/[0.08] dark:text-white"
              : isLight
                ? "border-border bg-surface-alt text-text-secondary dark:border-glass-border-10 dark:bg-white/6 dark:text-white/72"
                : "border-border bg-surface-alt text-text-secondary",
          )}
        >
          {icon ?? <div className="h-2 w-2 rounded-full bg-current" />}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p
              className={cn(
                "truncate font-semibold",
                size === "default" ? "text-[15px]" : "text-sm",
              )}
            >
              {label}
            </p>
            {badge ? (
              <span
                className={cn(
                  "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium",
                  active
                    ? isLight
                      ? "bg-surface-alt text-text-primary dark:bg-glass-bg-12 dark:text-white"
                      : "bg-brand/12 text-brand"
                    : isLight
                      ? "bg-surface-alt text-text-secondary dark:bg-glass-bg-10 dark:text-white/72"
                      : "bg-surface-alt text-text-secondary",
                )}
              >
                {badge}
              </span>
            ) : null}
          </div>

          {description ? (
            <p
              className={cn(
                "mt-0.5 line-clamp-1 text-xs leading-relaxed",
                active
                  ? isLight
                    ? "text-text-secondary dark:text-white/82"
                    : "text-text-secondary"
                  : isLight
                    ? "text-text-tertiary dark:text-white/58"
                    : "text-text-tertiary",
              )}
            >
              {description}
            </p>
          ) : null}
        </div>

        <div
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors",
            active
              ? isLight
                ? "border-border bg-surface-alt text-text-primary dark:border-white/16 dark:bg-glass-bg-12 dark:text-white"
                : "border-brand/15 bg-brand/10 text-brand"
              : isLight
                ? "border-border bg-surface-alt text-text-tertiary dark:border-glass-border-10 dark:bg-white/6 dark:text-white/52"
                : "border-border bg-surface-alt text-text-tertiary",
          )}
        >
          {active ? (
            <Check className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </div>
      </div>
    </button>
  );
}
